import React, { useMemo, useState } from "react";
import styled from "@emotion/styled";
import type { MenuProps } from "antd5";
import { App } from "antd5";
import { Dropdown } from "antd5/lib";

import { useDeleteOpportunity } from "../../../lib/hooks/api/opportunities/useDeleteOpportunity";
import { useOpportunityWorkflow } from "../../../lib/hooks/api/opportunities/useOpportunityWorkflow";
import { useBulkUpdateOpportunitiesStage } from "../../../lib/hooks/api/opportunities/useUpdateOpportunity";
import { Flex, Text } from "../../../styles/utility-components";
import ActionToast from "../../ui/action_toast/ActionToast";
import Button from "../../ui/button/Button";
import OpportunityDiscardModal from "../delete_opportunity_modal/OpportunityDiscardModal";
import { SelectedOpportunity } from "../FilterableOpportunities";
import { renderStageOption } from "../opportunity_stage/stageSelectUtils";
import { processTransitions } from "../opportunity_stage/utils";

type OpportunitiesActionBarProps = {
  selectedOpportunities: SelectedOpportunity[];
  onClearSelection: () => void;
};

export default function OpportunitiesActionBar({
  selectedOpportunities,
  onClearSelection,
}: OpportunitiesActionBarProps) {
  const { message } = App.useApp();
  const { data } = useOpportunityWorkflow();
  const transitions = data ? processTransitions(data.transitions) : [];

  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const selectedOpportunityIds = useMemo(
    () => selectedOpportunities.map((opp) => opp.id),
    [selectedOpportunities],
  );

  const { mutate: bulkUpdateStage } = useBulkUpdateOpportunitiesStage(
    {
      onSuccess: (_, variables) => {
        const stageName =
          transitions
            .flatMap((transition) => transition.options)
            .find((option) => option.value === variables.input.stageId)?.label || "Unknown";

        message.open({
          icon: null,
          content: (
            <ActionToast
              title={`${variables.input.opportunityIds.length} ${variables.input.opportunityIds.length === 1 ? "opportunity" : "opportunities"}`}
              subtitle={`changed to '${stageName}' stage`}
            />
          ),
        });
      },
      onError: () => {
        message.error("Failed to update opportunity stage");
      },
    },
    selectedOpportunities.map(({ id, stageId, name }) => ({ id, startStageId: stageId, name })),
  );

  const { mutate: deleteOpportunity } = useDeleteOpportunity({
    onSuccess: (_, variables) => {
      message.open({
        icon: null,
        content: (
          <ActionToast
            title={`${variables.ids.length} ${variables.ids.length === 1 ? "opportunity" : "opportunities"}`}
            subtitle="deleted"
          />
        ),
      });
    },
    onError: () => {
      message.error("Failed to delete opportunities");
    },
  });

  const handleStageChange = (stageId: string) => {
    if (!stageId) return;

    bulkUpdateStage({
      input: {
        opportunityIds: selectedOpportunityIds,
        stageId,
      },
    });
  };

  const handleDiscardModalSubmit = () => {
    deleteOpportunity({ ids: selectedOpportunityIds });
    onClearSelection();
  };

  const stageMenuItems: MenuProps["items"] = transitions.map((transition) => ({
    key: transition.label,
    type: "group",
    label: transition.label,
    children: transition.options.map((option) => ({
      key: option.value,
      label: renderStageOption(option.label, option.value, transitions),
      onClick: () => handleStageChange(option.value),
    })),
  }));

  return (
    <>
      <Container>
        <Flex gap={16} alignItems="center">
          <Text color="white">
            {selectedOpportunityIds.length}{" "}
            {selectedOpportunityIds.length === 1 ? "opportunity" : "opportunities"} selected:
          </Text>
          <ActionButtonWrapper>
            <Dropdown
              menu={{ items: stageMenuItems }}
              trigger={["click"]}
              dropdownRender={(menu) => <DropdownContainer>{menu}</DropdownContainer>}
            >
              <Button icon="alignTop02">Stage</Button>
            </Dropdown>
          </ActionButtonWrapper>
          <ActionButtonWrapper>
            <Button icon="trash01" onClick={() => setIsDiscardModalOpen(true)}>
              Delete
            </Button>
          </ActionButtonWrapper>
        </Flex>
        <Flex gap={8} alignItems="center">
          <Button icon="xClose" onClick={onClearSelection}>
            Deselect all
          </Button>
        </Flex>
      </Container>
      <OpportunityDiscardModal
        isOpen={isDiscardModalOpen}
        onCancel={() => setIsDiscardModalOpen(false)}
        onSubmit={handleDiscardModalSubmit}
        cancelText="Return to Opportunities"
        isPlural={selectedOpportunityIds.length > 1}
      />
    </>
  );
}

const Container = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",

  height: 48,

  padding: 8,

  backgroundColor: theme.colors.sysPrimaryDefault,
  borderRadius: 4,
  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.12)",
}));

const ActionButtonWrapper = styled.div(({ theme }) => ({
  border: `1px solid ${theme.colors.sysTextWhite}`,
  borderRadius: 4,
  position: "relative",
}));

const DropdownContainer = styled.div({
  maxHeight: 300,
  overflow: "auto",

  borderRadius: 8,
  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.12)",
});
